export const USE_CASES = [
  {
    id: "siteiq",
    name: "PyCray SiteIQ",
    badges: [
      { label: "Computer Vision + Workflow Automation", variant: "outline" },
      { label: "Claude Sonnet", variant: "filled" },
    ],
    whatItDoes: [
      "Upload site photos of any zone — room, floor, common area, trade area",
      "AI reads every photo against the defined trade-sequence checklist and gives an instant stage-by-stage completion estimate",
      "Cross-references photo evidence against the project schedule and supervisor's voice notes — flagging regressions, stalled work, and discrepancies automatically",
    ],
    businessBenefit: [
      "Real-time zone-by-zone completion percentages — no manual site audits required",
      "Catch stalled or regressed work before it becomes a costly delay",
      "One defensible, unified completion percentage across every zone and every project — updated every time a new photo comes in",
    ],
    cta: {
      label: "Try SiteIQ",
      href: "https://pycray-site-iq.vercel.app/",
    },
    industries: [
      {
        industry: "Residential Real Estate Developers",
        description:
          "Track progress across dozens of apartments, floors, and common areas simultaneously — AI detects which zones are on schedule, which have stalled, and which show signs of rework, without waiting for a weekly site visit report",
      },
      {
        industry: "Commercial Construction (PMC / TPI firms)",
        description:
          "Third-party inspectors upload photos during site visits — AI cross-references against the approved trade sequence and flags anything that doesn't match the verbal supervisor report, creating a defensible audit trail",
      },
      {
        industry: "Infrastructure Projects",
        description:
          "Highway, bridge, and civil contractors track work across multiple active zones in parallel — AI rolls up a weighted completion percentage for the entire project, updated in real time as new photos arrive",
      },
      {
        industry: "Solar & Industrial EPC",
        description:
          "EPC contractors monitor civil, structural, and electrical trade progress across large sites — AI catches trade sequence violations before they become compliance issues",
      },
      {
        industry: "Government / Public Sector Projects",
        description:
          "Departments overseeing multiple concurrent projects get a single live dashboard of completion percentages — without depending on manual progress reports that may be inaccurate or delayed",
      },
      {
        industry: "Real Estate Quality Auditors",
        description:
          "Quality audit firms conducting TPI inspections use AI to compare current site photos against prior visit records — automatically detecting regression, suspicious completion jumps, or verbal reports that contradict visual evidence",
      },
    ],
  },
  {
    id: "talentbridge",
    name: "PyCray TalentBridge",
    badges: [
      { label: "Document Intelligence + Semantic Matching", variant: "outline" },
      { label: "Claude Sonnet", variant: "filled" },
    ],
    whatItDoes: [
      "Upload a job description and up to 5 CVs",
      "AI ranks every candidate with a match score",
      "Get clear reasoning behind every decision",
    ],
    businessBenefit: [
      "Cut shortlisting time by 80%",
      "Never miss a strong candidate buried in a pile",
      "Make faster, more confident hiring decisions",
    ],
    cta: {
      label: "Try TalentBridge",
      href: "https://pycraytalentbridge.vercel.app/",
    },
    industries: [
      {
        industry: "IT / Tech",
        description:
          "Screen 200+ developer applications against exact stack requirements in minutes — no manual CV reading",
      },
      {
        industry: "Healthcare",
        description:
          "Filter candidates by mandatory certifications (BLS, ICU) before any human review begins",
      },
      {
        industry: "Manufacturing",
        description:
          "Identify ISO audit experience and safety certifications from unstructured CV text automatically",
      },
      {
        industry: "Retail",
        description:
          "Standardise hiring criteria across multiple locations so every manager uses the same objective benchmark",
      },
      {
        industry: "Consulting",
        description:
          "Extract domain exposure (BFSI, Supply Chain) from project descriptions and rank by pipeline fit",
      },
      {
        industry: "Construction",
        description:
          "Surface project scale experience and site safety qualifications from any CV format, any layout",
      },
    ],
  },
  {
    id: "buddybot",
    name: "PyCray BuddyBot",
    badges: [
      { label: "Conversational AI", variant: "outline" },
      { label: "Claude Sonnet", variant: "filled" },
    ],
    whatItDoes: [
      "Always-on conversational AI for your product or operations",
      "Understands your business context, products, and policies",
      "Handles queries, guides users, and escalates when needed",
    ],
    businessBenefit: [
      "24/7 intelligent responses — zero extra headcount",
      "Higher customer satisfaction, lower support cost",
      "Scales instantly with demand — no ramp-up time",
    ],
    cta: {
      label: "Try BuddyBot",
      href: "https://pycraybuddybot.vercel.app/",
    },
    industries: [
      {
        industry: "E-commerce / Retail",
        description:
          "Handles order status, return policy, and product queries — resolving 70% of support tickets without human intervention, around the clock",
      },
      {
        industry: "Real Estate",
        description:
          "Answers buyer queries on flat configurations, pricing, and possession dates instantly — qualifying leads before the sales team calls",
      },
      {
        industry: "Education / EdTech",
        description:
          "Responds to student queries on schedules, fees, and exam dates at 11pm before an exam — no office hours needed",
      },
      {
        industry: "Healthcare / Clinics",
        description:
          "Manages appointment queries, doctor availability, and insurance FAQs — reducing front desk call volume significantly",
      },
      {
        industry: "Financial Services",
        description:
          "Guides loan applicants through eligibility, documentation, and application status — cutting call centre load without sacrificing experience",
      },
      {
        industry: "Hospitality",
        description:
          "Handles guest queries on check-in, amenities, and local recommendations via WhatsApp or website — improving satisfaction scores instantly",
      },
    ],
  },
  {
    id: "pycray-kart",
    name: "PyCray Kart",
    badges: [
      { label: "Bilingual Voice + Chat Support", variant: "outline" },
      { label: "Deepgram + Claude", variant: "filled" },
    ],
    whatItDoes: [
      "Bilingual AI support agent for a neighbourhood ecommerce store — Hindi, English, and Hinglish",
      "Looks up orders by Order ID or phone and answers status, delivery ETA, warranty, and returns",
      "Voice and chat with graceful human escalation when the agent cannot resolve the query",
    ],
    businessBenefit: [
      "Resolve routine order queries 24/7 — no extra support headcount",
      "Customers get accurate answers from live order data, not scripted FAQs",
      "Smart escalation with reference numbers when a human agent is needed",
    ],
    cta: {
      label: "Try PyCray Kart",
      href: "https://pycray-kart.vercel.app/demos/supportbot",
    },
    industries: [
      {
        industry: "E-commerce / Retail",
        description:
          "Handles order tracking, delivery ETA, warranty checks, and return eligibility — resolving most support tickets without human intervention, in the customer's language",
      },
      {
        industry: "D2C Brands",
        description:
          "Gives small teams enterprise-grade support — customers share an Order ID or phone number and get instant, accurate answers from order data",
      },
      {
        industry: "Marketplace Sellers",
        description:
          "Manages post-purchase queries across multiple products — status, returns, and warranty — with automatic escalation for damaged or disputed orders",
      },
      {
        industry: "Consumer Electronics",
        description:
          "Answers warranty expiry, return window, and delivery questions for appliances and gadgets — reducing call centre load on high-volume SKUs",
      },
      {
        industry: "Grocery / Hyperlocal",
        description:
          "Tracks same-day and scheduled deliveries, COD status, and cancellation refunds — bilingual support for neighbourhood store customers",
      },
      {
        industry: "Omnichannel Retail",
        description:
          "Unifies online order support across channels — voice or chat, Hindi or English — with a single agent that escalates complex cases to your team",
      },
    ],
  },
  {
    id: "policypal",
    name: "PyCray PolicyPal",
    badges: [
      { label: "RAG Systems", variant: "outline" },
      { label: "Claude Sonnet", variant: "filled" },
    ],
    whatItDoes: [
      "Upload any company document — policy, manual, handbook, SOP",
      "Ask questions in plain English — no search, no scrolling",
      "Get instant, cited answers pulled directly from your document",
    ],
    businessBenefit: [
      "Kill repetitive HR and ops queries overnight",
      "New hires self-serve from day one — no hand-holding",
      "Your entire knowledge base, available 24/7",
    ],
    cta: {
      label: "Try PolicyPal",
      href: "https://pycray-policypal.vercel.app/demos/policypal",
    },
    industries: [
      {
        industry: "Manufacturing",
        description:
          "Workers query safety SOPs, machine operating procedures, and compliance checklists instantly — without calling the supervisor every time",
      },
      {
        industry: "Healthcare / Clinics",
        description:
          "Staff ask about patient handling protocols, drug administration guidelines, and shift policies without hunting through thick manuals",
      },
      {
        industry: "IT / Tech Companies",
        description:
          "Employees query HR policies, leave rules, reimbursement procedures, and onboarding checklists in seconds — reducing HR team interruptions",
      },
      {
        industry: "Real Estate / Construction",
        description:
          "Site teams access project specifications, quality checklists, and safety protocols on the go — even from a mobile device on site",
      },
      {
        industry: "Education / EdTech",
        description:
          "Teachers and admin staff query institutional policies, exam guidelines, and student handling procedures without waiting for management responses",
      },
      {
        industry: "Retail Chains",
        description:
          "Store managers query operational SOPs, returns policy, vendor guidelines, and staff conduct rules without escalating to HQ",
      },
    ],
  },
  {
    id: "flowiq",
    name: "PyCray FlowIQ",
    badges: [
      { label: "Workflow Automation", variant: "outline" },
      { label: "Claude Sonnet", variant: "filled" },
    ],
    whatItDoes: [
      "Upload any invoice — digital PDF, scanned, or handwritten photo",
      "AI extracts every field automatically — vendor, amount, line items, due date",
      "Get a structured output with classification and approval routing — ready to action",
    ],
    businessBenefit: [
      "Eliminate manual data entry from your finance workflow entirely",
      "Process invoices in seconds, not hours — zero errors, zero delays",
      "Free your team from repetitive paperwork — focus on work that matters",
    ],
    cta: {
      label: "Try FlowIQ",
      href: "https://pycray-flowiq.vercel.app/",
    },
    industries: [
      {
        industry: "Manufacturing",
        description:
          "Process hundreds of vendor invoices monthly — raw materials, machinery parts, consumables — automatically extracted, classified, and routed to the right approver without manual entry",
      },
      {
        industry: "Healthcare / Clinics",
        description:
          "Handle supplier invoices for medicines, equipment, and services — AI extracts line items, flags overdue payments, and routes high-value invoices to the finance head automatically",
      },
      {
        industry: "Retail Chains",
        description:
          "Process invoices from dozens of vendors across locations — AI classifies by category (packaging, logistics, store supplies) and routes for approval based on amount thresholds",
      },
      {
        industry: "Construction",
        description:
          "Extract data from supplier bills, subcontractor invoices, and material delivery notes — even handwritten ones from the field — and feed directly into project cost tracking",
      },
      {
        industry: "IT / Tech Companies",
        description:
          "Process SaaS subscriptions, vendor bills, and contractor invoices — AI tags by cost centre, flags renewals, and routes above-threshold invoices to the right approver",
      },
      {
        industry: "Logistics / Transport",
        description:
          "Handle fuel bills, toll receipts, driver expense claims, and vendor invoices — AI extracts, classifies, and outputs structured CSV ready for accounting software import",
      },
    ],
  },
];
